import { Link } from 'react-router-dom'
import { blogPosts } from '../../data/blog-posts'
import SEO from '../../components/SEO'
import Navbar from '../../components/Navbar'
import Footer from '../../components/Footer'
import { IconArrowRight } from '../../components/ui/Icon'
import TransferStrip from '../../components/ui/TransferStrip'
import { PRINT_PROCESS_I18N } from '../../data/print-process-i18n'
import { BLOG_IMAGE_SIZE, blogCopy, blogIndexPath, blogPostPath } from './blog-i18n'
import './blog.css'

/**
 * The three print-process station names for a service, in the page language.
 *
 * @param {string} service
 * @param {'tr'|'en'|'de'} lang
 */
function stationNames(service, lang) {
    const copy = PRINT_PROCESS_I18N[service]?.[lang] ?? PRINT_PROCESS_I18N.pad.tr
    return copy.stations.map((station) => station.name)
}

/**
 * One post as one row: the whole row is a single link. The first post is set
 * as the lead story (wide plate, display-scale title); the rest read as a
 * ruled list, so a single post still looks intentional and N posts scale.
 *
 * @param {{ post: Object, lang: 'tr'|'en'|'de', isLead: boolean, readMore: string }} props
 */
function PostRow({ post, lang, isLead, readMore }) {
    const copy = post.languages[lang]

    return (
        <li className={isLead ? 'blog-item blog-item--lead' : 'blog-item'}>
            <Link to={blogPostPath(lang, post.slug)} className="blog-row" viewTransition>
                {post.artwork ? (
                    <div className="blog-row-plate">
                        <TransferStrip labels={stationNames(post.artwork, lang)} />
                    </div>
                ) : post.image ? (
                    <div className="blog-row-plate plate plate--marks">
                        <img
                            src={post.image}
                            alt={copy.title}
                            width={BLOG_IMAGE_SIZE.width}
                            height={BLOG_IMAGE_SIZE.height}
                            loading={isLead ? 'eager' : 'lazy'}
                            decoding="async"
                        />
                    </div>
                ) : null}
                <h2 className="blog-row-title">{copy.title}</h2>
                <time className="blog-row-date legend tabular" dateTime={post.date}>
                    {post.date}
                </time>
                <div className="blog-row-aside">
                    <p className="blog-row-excerpt">{copy.excerpt}</p>
                    <span className="link-arrow">
                        {readMore}
                        <IconArrowRight />
                    </span>
                </div>
            </Link>
        </li>
    )
}

export default function BlogIndex({ lang = 'tr' }) {
    const t = blogCopy(lang)

    return (
        <>
            <SEO
                title={t.title}
                description={t.subtitle}
                lang={lang}
                url={blogIndexPath(lang)}
            />

            <Navbar lang={lang} />

            <main id="main-content" className="blog-index" lang={lang}>
                <header className="blog-head container">
                    <h1>{t.title}</h1>
                    <p className="lead">{t.subtitle}</p>
                </header>

                <div className="container">
                    <ol className="blog-list" aria-label={t.latestPosts}>
                        {blogPosts.map((post, index) => (
                            <PostRow
                                key={post.id}
                                post={post}
                                lang={lang}
                                isLead={index === 0}
                                readMore={t.readMore}
                            />
                        ))}
                    </ol>
                </div>
            </main>

            <Footer lang={lang} />
        </>
    )
}
